import React, { useCallback, useEffect, useRef } from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import Actions from 'Actions';
import checkoutUtils from 'utils/Checkout';
import GooglePay from 'utils/GooglePay';
import GooglePayEN from 'utils/locales/GooglePay_en_US';
import GooglePayFR from 'utils/locales/GooglePay_fr_CA';
import LanguageLocale from 'utils/LanguageLocale';
import store from 'Store';
import { useGooglePayBasket } from 'hooks/useGooglePayBasket';

import { buttons } from 'style/config';

const getText = LanguageLocale.getLocalisedDataFromResource(GooglePayEN, GooglePayFR);

const GooglePayButton = ({
    onClickOverride,
    useCheckoutPaymentsClient,
    isGooglePayPayment,
    isBopis,
    isSdd,
    transactionAmount,
    onRenderFailure,
    css,
    ...rest
}) => {
    const containerRef = useRef(null);

    const handleSuccess = useCallback(result => {
        checkoutUtils.placeOrderSuccess(result.orderResponse);
    }, []);

    const handleError = useCallback(() => {
        store.dispatch(
            Actions.showInfoModal({
                isOpen: true,
                isHtml: true,
                showCloseButton: true,
                title: getText('googlePayErrorTitle'),
                message: getText('googlePayErrorMessage'),
                buttonText: getText('googlePayErrorOkButton')
            })
        );
    }, []);

    const { onTap } = useGooglePayBasket({
        transactionAmount,
        transactionCurrencyCode: LanguageLocale.getCountryISOCurrency(),
        isBopis,
        isSdd,
        onSuccess: handleSuccess,
        onError: handleError
    });

    const effectiveClick = onClickOverride || onTap;

    useEffect(() => {
        if (isGooglePayPayment === GooglePay.TYPES.HIDDEN || !containerRef.current) {
            return undefined;
        }

        let buttonNode;

        try {
            const client = useCheckoutPaymentsClient ? GooglePay.getCheckoutPaymentsClient() : GooglePay.getPaymentsClient();
            // Google advised to use «short» as buttonType, which does not appear in their official guidelines:
            // https://developers.google.com/pay/api/web/guides/brand-guidelines
            buttonNode = client.createButton({
                onClick: effectiveClick,
                buttonType: 'short',
                buttonColor: 'black',
                buttonSizeMode: 'fill',
                buttonRadius: buttons.HEIGHT / 2,
                buttonBorderType: 'default_border',
                allowedPaymentMethods: GooglePay.getAllowedPaymentMethodsForButton()
            });
            containerRef.current.appendChild(buttonNode);
        } catch (err) {
            Sephora.logger.error('[GooglePay] createButton failed', err);

            if (typeof onRenderFailure === 'function') {
                onRenderFailure(err);
            }
        }

        return () => {
            if (buttonNode && buttonNode.parentNode === containerRef.current) {
                containerRef.current.removeChild(buttonNode);
            }
        };
    }, [isGooglePayPayment, effectiveClick, useCheckoutPaymentsClient, onRenderFailure]);

    if (isGooglePayPayment === GooglePay.TYPES.HIDDEN) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            data-at={Sephora.debug.dataAt('basket_googlepay_btn')}
            css={[containerStyle, css]}
            {...rest}
        />
    );
};

const containerStyle = {
    display: 'flex',
    width: '100%',
    height: buttons.HEIGHT,
    // The SDK-injected child is a flex item whose default min-width: auto
    // sets the parent to its largest width
    minWidth: 0
};

export default wrapFunctionalComponent(GooglePayButton, 'GooglePayButton');
